import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import JSZip from "jszip";
import { Download, ImageIcon, Loader2, UserPlus, Pencil } from "lucide-react";
import { usePrivatePage } from "../lib/seo";
import { Header } from "../components/Header";
import { SquadCard } from "../components/squad/SquadCard";
import { QuickAdd } from "../components/squad/QuickAdd";
import { ApplyLook } from "../components/squad/ApplyLook";
import { ShareBox, useShare } from "../components/squad/ShareBox";
import { api, errMsg, rememberTeam, rememberDraft } from "../lib/api";
import { preloadDesigns } from "../lib/images";
import { svgToPng, downloadBlob } from "../lib/exportPng";
import { defaultDesign, namedPlayer, SIZES, eur } from "../lib/shield";
import { squadPoster, isFinished } from "../lib/squadPoster";
import { raisedText } from "../components/coach/FundsSummary";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "player";

const Btn = ({ icon: Icon, busy, children, dark, ...p }) => (
  <button type="button" {...p} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-60 ${dark ? "bg-slate-900 text-white hover:bg-slate-700" : "border-2 border-slate-900 bg-white hover:bg-slate-100"}`}>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />} {children}
  </button>
);

export default function Squad() {
  usePrivatePage("Squad");
  const { id } = useParams();
  const nav = useNavigate();
  const refs = useRef({});
  const [team, setTeam] = useState(null);
  const [busy, setBusy] = useState("");
  const [lookFrom, setLookFrom] = useState("");
  const [funds, setFunds] = useState(null);
  useEffect(() => { api.get(`/teams/${id}/funds`).then((r) => setFunds(r.data)).catch(() => {}); }, [id]);
  const { share, reload: reloadShare } = useShare(id);

  const load = () => api.get(`/teams/${id}`).then(({ data }) => { setTeam(data); rememberTeam(data); setLookFrom((v) => v || data.players[0]?.id || ""); })
    .catch((e) => {
      toast.error(errMsg(e));
      nav([401, 403].includes(e?.response?.status) ? `/coach?next=/team/${id}` : "/coach");
    });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => { load(); reloadShare(); }, 30000); return () => clearInterval(t); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!team) return <div className="min-h-screen"><Header onNew={() => nav("/design")} /><div className="p-10 text-center text-slate-500">Loading squad…</div></div>;
  const players = team.players;
  const base = players.find((p) => p.id === lookFrom)?.design || players[0]?.design || defaultDesign();

  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };

  const addNames = (names) => run("add", async () => {
    for (const name of names) {
      const { data } = await api.post("/drafts", { title: name, design: namedPlayer(base, name), team_id: id });
      rememberDraft(data);
    }
    toast.success(`Added ${names.length} player${names.length > 1 ? "s" : ""}`);
    await load();
  });

  const addInDesigner = () => run("one", async () => {
    const { data } = await api.post("/drafts", { title: "Untitled shield", design: namedPlayer(base, ""), team_id: id });
    nav(`/d/${data.id}`);
  });

  const applyLook = () => run("look", async () => {
    const { data } = await api.post(`/teams/${id}/apply-look`, { source_draft_id: lookFrom });
    toast.success(`Club look applied to ${data.updated} other player${data.updated === 1 ? "" : "s"}`);
    await load();
  });

  const rename = () => {
    const name = window.prompt("Squad name", team.name);
    if (name?.trim()) run("rename", async () => { await api.put(`/teams/${id}`, { name: name.trim() }); await load(); });
  };

  const remove = (pid) => window.confirm("Remove this player from the squad? Their shield draft is kept.") && run("remove", async () => { await api.put(`/drafts/${pid}/team`, { team_id: null }); await load(); });

  const release = (pid) => window.confirm("Let another parent edit this shield? The current parent will lose edit access.") && run("release", async () => { await api.post(`/teams/${id}/players/${pid}/release`); await reloadShare(); toast.success("Released"); });

  const downloadAll = () => run("zip", async () => {
    await preloadDesigns(players.map((p) => p.design));
    const zip = new JSZip();
    for (const [i, p] of players.entries()) {
      const svg = refs.current[p.id];
      if (svg) zip.file(`${String(i + 1).padStart(2, "0")}-${slug(p.title)}.png`, await svgToPng(svg));
    }
    downloadBlob(await zip.generateAsync({ type: "blob" }), `${slug(team.name)}-shields.zip`);
  });

  const finished = players.filter(isFinished);
  const downloadPoster = () => run("poster", async () => {
    await preloadDesigns(finished.map((p) => p.design));
    const blob = await squadPoster(finished.map((p) => refs.current[p.id]).filter(Boolean), { club: base.player?.clubName, squad: team.name });
    downloadBlob(blob, `${slug(team.name)}-squad-poster.png`);
    toast.success(`Poster saved with ${finished.length} shield${finished.length === 1 ? "" : "s"}`);
  });

  return (
    <div className="min-h-screen pb-10">
      <Header onNew={() => nav("/design")} />
      <main className="mx-auto max-w-[1500px] px-4 py-6 sm:px-6 lg:py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow">Team batch · squad {team.id}</div>
            <h1 className="font-display flex items-center gap-3 text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl" data-testid="squad-name">
              {team.name}
              <button type="button" onClick={rename} aria-label="Rename squad" data-testid="squad-rename-button" className="text-slate-400 hover:text-slate-900"><Pencil className="h-5 w-5" /></button>
            </h1>
            <p className="mt-2 text-sm text-slate-600" data-testid="squad-count">{players.length} player{players.length === 1 ? "" : "s"} sharing one club look</p>
            <p className="mt-1 text-sm font-extrabold text-lime-700" data-testid="squad-raised">{raisedText(funds)}</p>
            <p className="mt-1 text-xs font-semibold text-slate-500" data-testid="squad-price-note">Parents pay: from {eur(Math.min(...SIZES.map((s) => s.price)))} per shield <a href="/shields#size-chart" className="underline">· sizes & prices</a></p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn icon={Download} busy={busy === "zip"} disabled={!players.length || !!busy} onClick={downloadAll} dark data-testid="squad-download-all-button">Download all (ZIP)</Btn>
            <Btn icon={ImageIcon} busy={busy === "poster"} disabled={!finished.length || !!busy} onClick={downloadPoster} title={finished.length ? "Square 1080×1080 image for social media" : "Shields need a photo and name to appear on the poster"} data-testid="squad-poster-button">Squad poster ({finished.length})</Btn>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[340px_1fr]">
          <aside className="space-y-5 self-start border-2 border-slate-900 bg-white p-5 shadow-[6px_6px_0_0_#0f172a] lg:sticky lg:top-24">
            <QuickAdd onAdd={addNames} busy={busy === "add"} />
            {players.length > 1 && <ApplyLook players={players} value={lookFrom} onPick={setLookFrom} onApply={applyLook} busy={busy === "look"} pulse={!team.look_applied} />}
            <Btn icon={UserPlus} busy={busy === "one"} onClick={addInDesigner} data-testid="squad-add-in-designer-button">Add player in designer</Btn>
            <ShareBox teamId={id} share={share} onChange={reloadShare} />
          </aside>
          <section className="grid content-start grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4" data-testid="squad-grid">
            {players.length === 0 && <p className="col-span-full py-10 text-center text-slate-500" data-testid="squad-empty">No players yet – add your squad on the left.</p>}
            {players.map((p) => <SquadCard key={p.id} player={p} svgRef={(el) => { refs.current[p.id] = el; }} onRemove={() => remove(p.id)} claimed={share?.claimed.includes(p.id)} onRelease={() => release(p.id)} />)}
          </section>
        </div>
      </main>
    </div>
  );
}
